import EastIcon from '@mui/icons-material/East';
import { TFunction } from 'i18next';
import BookmarkIcon from 'public/icons/icn-bookmark.svg';
import { ReactElement } from 'react';

import { Category } from '../types/category.types';
import { capitalize } from './dom-element-helpers';
import { ParsedUserSavedSearchQuery } from './userSavedSearchConverters';

export type CategorySearchSuggestion = {
  category: Category;
  topCategory: Category | null;
  categoryTitlePath: string;
  score: number;
  matches: string[];
};

const searchCategories = (
  categories: Category[],
  searchKeyWords: string[],
): {
  foundCategories: CategorySearchSuggestion[];
  maxScore: number;
} => {
  if (!categories || !categories.length || !searchKeyWords) {
    return {
      foundCategories: [],
      maxScore: 0,
    };
  }

  const foundCategories: CategorySearchSuggestion[] = [];
  let maxScore = 0;

  // Searching categories.
  for (const category of categories) {
    const matches = searchKeyWords.filter((searchKeyWord) => category.categoryTitlePath.includes(searchKeyWord));
    const score = (matches.length / searchKeyWords.length) * 100;

    // Found result.
    if (matches.length > 0) {
      foundCategories.push({
        category,
        topCategory: category.topCategory || null,
        categoryTitlePath: category.categoryTitlePath,
        score,
        matches,
      });

      // Check for new max level.
      maxScore = Math.max(maxScore, score);
    }
  }

  return { foundCategories, maxScore };
};

const getCleanSearchKeywords = (searchKeyWord: string) =>
  searchKeyWord //
    ?.replace(/[^a-zA-Z\u00C0-\u017F\s]/g, '') // Keep only letters (including latin-letters) and spaces.
    .split(' ')
    .map((keyword) => keyword.trim().toLowerCase())
    .filter((exists) => !!exists);

export const getCategorySearchSuggestions = (categories: Category[], searchKeyWord: string): CategorySearchSuggestion[] => {
  const searchKeywords = getCleanSearchKeywords(searchKeyWord);

  const { foundCategories, maxScore } = searchCategories(categories, searchKeywords);
  const mostAccurateFoundCategories = foundCategories.filter((category) => category.score === maxScore);

  // Do not remove this console log as this is main helpful one to help you debug and/or improve.
  // console.log('SUGGESTIONS', { searchKeywords, foundCategories, maxScore, mostAccurateFoundCategories });

  return mostAccurateFoundCategories;
};

export const getCategorySuggestionTitle = (categorySuggestion: CategorySearchSuggestion, searchKeyWord?: string): ReactElement => {
  const categoryTopTitle = categorySuggestion?.topCategory?.title;
  const categoryTitle = categorySuggestion?.category?.title;
  const searchKeywords = getCleanSearchKeywords(searchKeyWord);

  let categoryTopTitleHighlighted = categoryTopTitle;
  let categoryTitleHighlighted = categoryTitle;

  searchKeywords?.forEach((searchKey) => {
    categoryTopTitleHighlighted = categoryTopTitleHighlighted?.replace(new RegExp(searchKey, 'ig'), (match) => `<b>${match}</b>`);
    categoryTitleHighlighted = categoryTitleHighlighted.replace(new RegExp(searchKey, 'ig'), (match) => `<b>${match}</b>`);
  });

  return (
    <>
      {categoryTopTitle && (
        <>
          <span className="top-category" dangerouslySetInnerHTML={{ __html: categoryTopTitleHighlighted }} />
          <EastIcon />
        </>
      )}
      <span className="category" dangerouslySetInnerHTML={{ __html: categoryTitleHighlighted }} />
    </>
  );
};

export const getUserSavedSearchSuggestion = ({
  categoryTitle,
  topCategoryTitle,
  tagTitle,
  brandTitle,
  searchKey,
  query,
  conditionsIdNameMap,
  colorsIdNameMap,
  materialsIdNameMap,
  formatPrice,
  t,
}: {
  categoryTitle?: string;
  topCategoryTitle?: string;
  tagTitle?: string; // For tag pages.
  brandTitle?: string; // For brand pages.
  searchKey?: string;
  query?: Partial<ParsedUserSavedSearchQuery>;
  conditionsIdNameMap: Record<number, string>;
  colorsIdNameMap: Record<number, string>;
  materialsIdNameMap: Record<number, string>;
  formatPrice: (price: string | number) => string;
  t: TFunction;
}): ReactElement => {
  const mainLabels = [
    searchKey ? `"${searchKey}"` : null,
    topCategoryTitle || categoryTitle ? (
      <>
        {topCategoryTitle && (
          <>
            <span className="saved-search main-label capitalize">{topCategoryTitle}</span>
            <EastIcon style={{ verticalAlign: 'middle' }} />
          </>
        )}
        <span className="category saved-search">{categoryTitle}</span>
      </>
    ) : null,
    tagTitle ? capitalize(tagTitle) : null,
  ]?.filter((exists) => !!exists);

  const [mainLabel, ...otherMainLabels] = mainLabels;

  const queryDataStr = [
    // Brand.
    brandTitle,

    // Sizes.
    query?.sizes?.join(', '),

    // Conditions.
    query?.conditionIds
      ?.map((conditionId) => conditionsIdNameMap?.[conditionId]?.trim())
      ?.filter((exists) => !!exists)
      ?.join(', '),

    // Colors.
    query?.colorIds
      ?.map((colorId) => colorsIdNameMap?.[colorId]?.trim())
      ?.filter((exists) => !!exists)
      ?.join(', '),

    // Materials.
    query?.materialIds
      ?.map((materialId) => materialsIdNameMap?.[materialId]?.trim())
      ?.filter((exists) => !!exists)
      ?.join(', '),

    // Price range.
    [
      (query?.minPrice || query?.maxPrice) && formatPrice(query?.minPrice || 0),
      query?.maxPrice ? formatPrice(query?.maxPrice) : query?.minPrice ? '+' : '',
    ]
      .filter((exists) => !!exists)
      .join(query?.maxPrice ? ' - ' : ''),
  ]
    .filter((exists) => !!exists)
    .join(', ')
    .trim();

  return (
    <div className="saved-search-row">
      <div className="saved-search-row-inner">
        {/* Main label. */}
        <div className="saved-search main-label capitalize">{mainLabel || t('components.search.all-categories')}</div>

        {/* Gray text. */}
        <span className="saved-search additional-data" title={queryDataStr}>
          {otherMainLabels.map((label, index) => (
            <>
              {label}
              {index < otherMainLabels.length && queryDataStr?.length ? <>,&nbsp;</> : null}
            </>
          ))}
          {queryDataStr}
        </span>
      </div>

      <div className="bookmark-icon">
        <BookmarkIcon />
      </div>
    </div>
  );
};
